import {useQuery} from '@tanstack/react-query'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const appOrigin = getAppOrigin()
const isServer = typeof window === 'undefined'
const PHPKB_BASE_URL = 'https://delonghi.knowledgebase.co/api-kb'
const safe = (v) => encodeURIComponent(v || '')

const PHP_BRAND_NAME_VARIATIONS = {
    nutribullet: 'capitalbrands',
}

/**
 * Custom hook to interact with PHPKB API.
 * @returns {Object} - An object containing function that make specific call to the PHPKB APO.
 */

const usePHPBKApi = () => {
    // Extract locale and site info
    const {locale, site} = useMultiSite()
    const siteBrand = PHP_BRAND_NAME_VARIATIONS[site?.id.split('-')[0]] || site?.id.split('-')[0]
    const localeData = locale?.id.split('-')
    const siteCountry = localeData[1]
    const siteLang = localeData[0]

    /**
     * Util for post API calls (client-side: goes through Express proxy).
     * @param {string} endpoint - The API endpoint to call.
     * @param {Object} body - The request body to send.
     * @returns {Promise<Object>} - The response data.
     * @throws {Error} - If the request fails.
     */

    const postData = async (endpoint, body) => {
        const res = await fetch(`${appOrigin}${MRT_API_BASEPATH}/${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        })
        if (!res.ok) {
            throw new Error(`Failed to fetch ${endpoint}`)
        }
        return res.json()
    }

    /**
     * Util for direct PHPKB API calls (SSR: bypasses Express proxy to avoid self-fetch).
     * @param {string} path - The PHPKB API path (e.g. '/categories').
     * @param {string} queryString - The query string parameters.
     * @returns {Promise<Object>} - The response data.
     * @throws {Error} - If the request fails.
     */
    const fetchPhpkbDirect = async (path, queryString) => {
        const res = await fetch(`${PHPKB_BASE_URL}${path}?${queryString}`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json'},
        })
        if (!res.ok) {
            throw new Error(`Failed to fetch PHPKB ${path}`)
        }
        return res.json()
    }
    /**
     * Searches for articles based on user input
     * @param {Object} params - The query parameters.
     * @param {string} params.q - String to search for.
     * @param {string} [params.category_id] -(Optional) Id of the category in which to look. The search will also be done in the sub-category.
     * @param {number} [params.from] - (Optional) The number of page from where to start.
     * @param {number} [params.limit] - (Optional) Number of maximum articles to return.
     * @param {Object} options - The query options.
     * @returns {Object} - The service returns a JSON object with search information and field data.
     */

    const usePHPBKFreeTextSearch = (params, options) => {
        const {q, category_id = null, from = null, limit = null} = params

        return useQuery(
            ['useFreeTextQuery', {q, category_id, from, limit}],
            () => {
                if (isServer) {
                    let qs = `q=${safe(q)}&lang=${safe(siteLang)}&country=${safe(
                        siteCountry,
                    )}&brand=${safe(siteBrand)}`
                    if (category_id) qs += `&catid=${safe(category_id)}`
                    if (from) qs += `&from=${safe(from)}`
                    if (limit) qs += `&limit=${safe(limit)}`
                    return fetchPhpkbDirect('/articles', qs)
                }
                return postData('get-phpkb/freetext-search', {
                    q,
                    category_id,
                    from,
                    limit,
                    country: siteCountry,
                    lang: siteLang,
                    brand: siteBrand,
                })
            },
            options,
        )
    }
    /**
     * This function returns the topic / subtopic tree filtered by coutnry and language
     * Language brand and country are automatically added from the useMultiste
     * @param {Object} params - The Query params
     * @param {String} params.name - The category from which to start building the tree, the values are: (WSS,KA)
     * @returns {Object} - Returns a JSON Object
     */

    const usePHPKBbyCountryAndLanguage = (params, options) => {
        const {name} = params
        return useQuery(
            ['useLanguageAndCountryQuery', {name}],
            () => {
                if (isServer) {
                    const qs = `lang=${safe(siteLang)}&country=${safe(siteCountry)}&brand=${safe(
                        siteBrand,
                    )}`
                    return fetchPhpkbDirect('/categories', qs)
                }
                return postData('get-phpkb/countries', {
                    lang: siteLang,
                    country: siteCountry,
                    name,
                    brand: siteBrand,
                })
            },
            options,
        )
    }

    /**
     * This function returns a single article using its ID
     * @param {Object} params - The parameters of the query
     * @param {String} params.article_id - The id of the article for which information is to be retrieved
     * @param {Object} options - The option of the query
     * @returns {Object} - Returns JSON
     */
    const usePHPKBGetArticle = (params, options) => {
        const {article_id} = params
        return useQuery(
            ['useGetArticleQuery', {article_id}],
            () => {
                if (isServer) {
                    return fetchPhpkbDirect(`/articles/${safe(article_id)}`, '')
                }
                return postData('get-phpkb/articles/article', {article_id})
            },
            options,
        )
    }

    /**
     * Returns information about a category and the articles within
     * @param {Object} params - The parameters of the query
     * @param {String} params.category_id - The id of the article for which information is to be retrieved
     * @param {Number} params.from -(optional) initial value for pagination (does not work)
     * @param {Number} param.limit (optional) number of values to return (does not work)
     * @param {Object} options - The option of the query
     * @returns {Object} - Returns JSON
     */

    const usePHPKBGetArticleFromCategory = (params, options) => {
        const {category_id, from, limit} = params
        return useQuery(
            ['useGetArticleFromCategory', {category_id, from, limit}],
            () => {
                if (isServer) {
                    return fetchPhpkbDirect(`/categories/${safe(category_id)}`, '')
                }
                return postData('get-phpkb/articles/article-from-category', {
                    category_id,
                    from,
                    limit,
                })
            },
            options,
        )
    }

    /**
     * Returns, given the id of an article, the list of all the articles that are one of ittranslation.
     * @param {Object} params - The parameters of the query
     * @param {String} params.article_id - The id of the article for which information is to be retrieved
     * @param {Object} options - The option of the query
     * @returns {Object} - Returns JSON
     */
    const useGetPHPKBLinkedArtictles = (params, options) => {
        const {article_id} = params
        return useQuery(
            ['useGetLinkedArticles', {article_id}],
            () => {
                if (isServer) {
                    return fetchPhpkbDirect(`/linked-articles/${safe(article_id)}`, '')
                }
                return postData('get-phpkb/articles/linked-articles', {article_id})
            },
            options,
        )
    }

    return {
        usePHPBKFreeTextSearch,
        usePHPKBbyCountryAndLanguage,
        usePHPKBGetArticle,
        usePHPKBGetArticleFromCategory,
        useGetPHPKBLinkedArtictles,
    }
}

export default usePHPBKApi
